import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { OfferOption } from 'ng2-app/models/dto/offer-option/offer-option.model';
import { PatchOfferOptionStatus, RecommendOfferOption } from 'ng2-app/store/offers/offer-options.actions';
import { Observable} from 'rxjs';
import { take } from 'rxjs/operators';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'offer-option',
  templateUrl: './offer-option.component.html',
  styleUrls: ['./offer-option.component.scss'],
  standalone: false
})
export class OfferOptionComponent implements OnInit {

    @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

    @Input() option: OfferOption;
    @Input() optionType: string;
    @Input() cardExpandable: boolean;
    @Output() acceptedToggle = new EventEmitter<{ id: number, checked: boolean }>();
    @Input() hasContractRequest: boolean;

    user: LenderUser;

    isAchTermSet$: boolean;
    termDurationFormatted: string;
    optionParentNumber: number;
    optionParentName: string;
    optionParentExpirationDate: Date;
    optionParentLenderName: string;
    optionParentType: string;
    optionLoanCategoryType: string;

    canViewOfferCuration: boolean;
    canCreateContractRequest: boolean;
    optionCreatedFormatted: Date;
    parentExpiredFormatted: Date;
    offerIsVisible: boolean;
    canRecommendOptions: boolean;

    constructor(
      private store: Store,
    ) { }

    async ngOnInit() {
      let nameField;
      let expirationField;
      let lenderNameField;
      let typeField;

      this.optionLoanCategoryType = this.option?.deal?.loan_product?.category?.type || '';

      if(this.optionType === 'approval'){
        nameField = 'loanProductName';
        expirationField = 'expires';
        lenderNameField = 'loanProductLender';
        typeField = 'stability';
        this.optionParentNumber = this.option.approval?.id || 0;
        this.optionParentName = this.option.approval?.[nameField];
        this.optionParentExpirationDate = this.option.approval?.[expirationField];
        this.optionParentLenderName = this.option.approval?.[lenderNameField];
        this.optionParentType = this.option.approval?.[typeField];
      }

      this.optionCreatedFormatted = new Date(this.option.created);
      if (this.optionParentExpirationDate) {
        const expirationDate = new Date(this.optionParentExpirationDate);
        if (this.isValidDate(expirationDate)) {
          this.parentExpiredFormatted = expirationDate;
        }
      }

      this.lenderUser$.pipe(take(1)).subscribe(user =>{
        this.user = user;
      });

      this.canCreateContractRequest = true;

      const invalidStagesForCRCreate = ['application','funded','inactive'];
      const invalidStatusesForCRCreate = ['awaitingResponse','declined'];

      this.canCreateContractRequest = !((invalidStagesForCRCreate.some(stage => stage === this.option.deal?.stage)) ||
        (invalidStatusesForCRCreate.some(status => status === this.option.deal?.status))
      );

      //assume ACH for now until other types are being implemented
      //when have multiple types we'll want to enhance the model, use classes or structural checking to determine type different
      this.isAchTermSet$ = true;
      this.canViewOfferCuration = this.user.permissions.includes('ff:offerCuration');
      this.canRecommendOptions = !['inactive', 'funded'].includes(this.option.deal?.stage || '')
        && this.user.permissions.includes('recommendFinancingOptions');
      switch (this.option.termSet.termType) {
        case 'months' : this.termDurationFormatted = 'mo.'; break;
        case 'weeks' :  this.termDurationFormatted = 'wk.'; break;
        case 'days' :
           this.termDurationFormatted = 'day';
           this.termDurationFormatted += (this.option.termSet.term > 1) ? 's' : ''; break;
        default : this.termDurationFormatted = ''; break;
      }
      this.offerIsVisible = this.canViewOffer();
    }

    handleOfferOptionUpdated(updatedOfferData){
      this.option.termSet.term = updatedOfferData.term;
      this.option.termSet.paymentFrequency = updatedOfferData.paymentFrequency;
      this.option.termSet.points = updatedOfferData.points;
      this.option.termSet.paymentCount = updatedOfferData.paymentCount;
      this.option.termSet.paymentAmount = updatedOfferData.paymentAmount;
      this.option.termSet.disbursement = updatedOfferData.disbursement;
      this.option.termSet.commission = updatedOfferData.commission;

      switch (this.optionLoanCategoryType) {
        case 'loc':
          this.setLocOptionTermset(this.option, updatedOfferData);
          break;
        case 'term':
          this.setTermOptionTermset(this.option, updatedOfferData);
          break;
        case 'ach':
        default:
          this.setAchOptionTermset(this.option, updatedOfferData);
      }
    }

    acceptedToggleChange(checked: boolean) {
      const status = checked ? 'accepted' : 'declined';
      this.store.dispatch( new PatchOfferOptionStatus(this.option.dealId, this.option.id, status) );
    }

    recommendedToggleChange(event: MatSlideToggleChange) {
      this.store.dispatch(new RecommendOfferOption(this.option.id, event.checked));
    }

    isValidDate(date: Date): boolean {
      return date instanceof Date && !isNaN(date.getTime());
    }

    private canViewOffer() {
      if (this.canViewOfferCuration && this.optionType === 'approval') {
        return this.optionParentNumber === this.option.approvalId;
      }
      return this.optionParentNumber === this.option.offerId;
    }

      private setAchOptionTermset(option: OfferOption, updatedOfferData): OfferOption {
        option.termSet.amount = updatedOfferData.amount;
        option.termSet.originationFeePercent = updatedOfferData.originationFeePercent;
        option.termSet.factorRate = updatedOfferData.factorRate;
        option.termSet.payback = updatedOfferData.payback;
        option.termSet.originationFeeAmount = updatedOfferData.originationFeeAmount;
        return option;
      }
      private setLocOptionTermset(option: OfferOption, updatedOfferData): OfferOption {
        option.termSet.creditLimit = updatedOfferData.creditLimit;
        option.termSet.initialDrawAmount = updatedOfferData.initialDrawAmount;
        option.termSet.periodicRate = updatedOfferData.periodicRate;
        option.termSet.periodsPerYear = updatedOfferData.periodsPerYear;
        option.termSet.annualInterestRate = updatedOfferData.annualInterestRate;
        option.termSet.apr = updatedOfferData.apr;
        option.termSet.drawFeePercent = updatedOfferData.drawFeePercent;
        option.termSet.drawFeeAmount = updatedOfferData.drawFeeAmount;
        return option;
      }
      private setTermOptionTermset(option: OfferOption, updatedOfferData): OfferOption {
        option.termSet.amount = updatedOfferData.amount;
        option.termSet.periodicRate = updatedOfferData.periodicRate;
        option.termSet.periodsPerYear = updatedOfferData.periodsPerYear;
        option.termSet.annualInterestRate = updatedOfferData.annualInterestRate;
        option.termSet.apr = updatedOfferData.apr;
        option.termSet.originationFeePercent = updatedOfferData.originationFeePercent;
        option.termSet.originationFeeAmount = updatedOfferData.originationFeeAmount;
        return option;
      }
}
